/* eslint-disable react/prop-types */
import { useRef, useState, useMemo } from "react";
import {
  Camera, Image as ImageIcon, Video as VideoIcon, FileText,
  ExternalLink, Trash2,
} from "lucide-react";
import { uploadFile, uploadUrl } from "../../../lib/uploads";
import MediaLightbox from "../../../components/MediaLightbox";

/**
 * TicketMediaSection — Phase 7 Sub-iter 7A.
 *
 * Photos, videos & documents attached to a ticket.  Uses ticket's
 * inline `attachments[]` array (server stores it on the ticket doc).
 * Trust Contract Rule 2: inline 2-step delete confirm.
 */
export default function TicketMediaSection({ t, user, isManager, onReload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState({ done: 0, total: 0 });
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Iter 418.aa (Dirk, 2026-02-28) — lightbox state. Clicking a photo
  // used to `window.open(rawR2Url)` which opened fullscreen on mobile
  // with no browser chrome / close affordance — user had to kill the
  // whole app. Now every image/video opens in the app's MediaLightbox
  // which has a proper close (X, backdrop tap, Escape key).
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const lightboxItems = useMemo(() => {
    const items = (t.attachments || [])
      .filter((att) => {
        const ct = att.content_type || "";
        return ct.startsWith("image/") || ct.startsWith("video/") || att.kind === "image" || att.kind === "video";
      })
      .map((att) => ({
        id: att.id,
        // Iter 418.bh (Dirk, 2026-02-28) — MediaLightbox expects `.src`
        // (see MediaLightbox.jsx line 233 `<img src={current.src}>`).
        // We were passing `url` so the <img> got `src={undefined}`
        // and the lightbox rendered a black screen. Also ship `kind`
        // + `contentType` so the video-detect branch works.
        src: uploadUrl(att.id),
        kind: (att.content_type || "").startsWith("video/") || att.kind === "video" ? "video" : "image",
        contentType: att.content_type,
        filename: att.filename,
        caption: att.filename,
      }));
    return items;
  }, [t.attachments]);

  const handleAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading({ done: 0, total: files.length });
    let i = 0;
    for (const f of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const up = await uploadFile(f);
        // eslint-disable-next-line no-await-in-loop
        await (await import("../../../lib/api")).default.post(
          `/tickets/${t.id}/attachments`,
          {
            id: up.id,
            filename: up.filename,
            content_type: up.content_type,
            size: up.size,
            kind: up.kind || "file",
          },
        );
      } catch (err) {
        console.error("Upload failed", err);
      }
      i += 1;
      setUploading({ done: i, total: files.length });
    }
    setUploading({ done: 0, total: 0 });
    onReload();
  };

  const onDelete = async (att) => {
    const api = (await import("../../../lib/api")).default;
    await api.delete(`/tickets/${t.id}/attachments/${att.id}`);
    setConfirmDelete(null);
    onReload();
  };

  return (
    <div className="hv-card p-5" data-testid="ticket-media-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display text-xl font-bold flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#1fb6c1]" /> Photos & videos
          {(t.attachments || []).length > 0 && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
              {t.attachments.length}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {uploading.total > 0 && (
            <span className="text-xs text-[#1fb6c1]" data-testid="ticket-upload-progress">
              Uploading {uploading.done}/{uploading.total}…
            </span>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="hv-btn hv-btn-primary"
            data-testid="ticket-media-upload-btn"
            disabled={uploading.total > 0}
          >
            <Camera className="w-4 h-4" /> Upload media
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,video/*,application/pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
            capture="environment"
            onChange={handleAttachments}
            className="hidden"
            data-testid="ticket-media-input"
          />
        </div>
      </div>

      {(t.attachments || []).length === 0 ? (
        <div className="text-sm text-[#647087] italic py-3">
          No photos, videos or files attached yet. Upload images straight from your camera, videos, or document files.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {t.attachments.map((att) => {
            const idxInLightbox = lightboxItems.findIndex((it) => it.id === att.id);
            return (
              <TicketAttachmentTile
                key={att.id}
                att={att}
                canDelete={isManager || att.uploaded_by_id === user?.id}
                confirmingDelete={confirmDelete === att.id}
                onAskDelete={() => setConfirmDelete(att.id)}
                onCancelDelete={() => setConfirmDelete(null)}
                onConfirmDelete={() => onDelete(att)}
                onOpen={idxInLightbox >= 0 ? () => setLightboxIndex(idxInLightbox) : null}
              />
            );
          })}
        </div>
      )}
      {lightboxIndex >= 0 && (
        <MediaLightbox
          items={lightboxItems}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
    </div>
  );
}

function TicketAttachmentTile({
  att, canDelete, confirmingDelete, onAskDelete, onCancelDelete, onConfirmDelete, onOpen,
}) {
  const url = uploadUrl(att.id);
  const ct = att.content_type || "";
  const isImage = ct.startsWith("image/") || att.kind === "image";
  const isVideo = ct.startsWith("video/") || att.kind === "video";

  return (
    <div
      className="group relative border border-white/10 rounded-md overflow-hidden bg-white/[0.02]"
      data-testid={`ticket-attachment-${att.id}`}
    >
      {/* Iter 418.aa — image/video tiles open the in-app lightbox
          (which has a proper Close/backdrop/Escape); everything else
          falls back to the raw file link. */}
      {(isImage || isVideo) && onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full"
          data-testid={`ticket-attachment-open-${att.id}`}
          title="Open in viewer"
        >
          {isImage ? (
            <img src={url} alt={att.filename} className="w-full aspect-square object-cover" />
          ) : (
            <video
              src={url}
              className="w-full aspect-square object-cover bg-black"
              preload="metadata"
              muted
              playsInline
            />
          )}
        </button>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block">
          <div className="w-full aspect-square flex flex-col items-center justify-center gap-1 text-[#647087] p-2">
            <FileText className="w-7 h-7" />
            <div className="text-[10px] truncate w-full text-center">{att.filename}</div>
          </div>
        </a>
      )}
      <div className="px-2 py-1.5 flex items-center justify-between gap-1 border-t border-white/5 bg-black/30">
        <span className="text-[10px] truncate text-[#a0aabf]" title={att.filename}>
          {isImage ? <ImageIcon className="w-3 h-3 inline mr-1" /> : isVideo ? <VideoIcon className="w-3 h-3 inline mr-1" /> : null}
          {att.filename}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#647087] hover:text-[#1fb6c1]"
            title="Open"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
          {canDelete && (
            confirmingDelete ? (
              <>
                <button
                  type="button"
                  onClick={onConfirmDelete}
                  className="text-[10px] text-rose-300"
                  data-testid={`ticket-attachment-delete-confirm-${att.id}`}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={onCancelDelete}
                  className="text-[10px] text-[#647087]"
                  data-testid={`ticket-attachment-delete-cancel-${att.id}`}
                >
                  ✕
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onAskDelete}
                className="text-[#647087] hover:text-rose-300"
                title="Delete"
                data-testid={`ticket-attachment-delete-${att.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )
          )}
        </div>
      </div>
      {isVideo && (
        <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1 py-0.5">
          <VideoIcon className="w-3 h-3 inline" />
        </span>
      )}
    </div>
  );
}
